import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import type { ReactElement, ReactNode } from 'react';
import { useConnectionNotification } from '../../hooks/ui/useConnectionNotification';
import useBatteryNotifications, { BatteryNotificationDevice } from '../../hooks/ui/useBatteryNotifications';
import { useControllerFirmwareUpdateNotification } from '../../hooks/ui/useControllerFirmwareUpdateNotification';
import ControllerFirmwareUpdateMessage from './ControllerFirmwareUpdateMessage';
import { bottomCenterSnackbarStackSx, centeredPlainSnackbarContentSx, defaultSnackbarContentSx, mergeSx } from './snackbarStyles';
import ControllerBatteryIcon from '../Icons/Power/controller/controller_level1.svg?react';
import DriveBaseBatteryIcon from '../Icons/Power/robot/robot_level1.svg?react';

const AUTO_HIDE_DURATION_MS = 5000;
const CONNECTION_AUTO_HIDE_DURATION_MS = 3500;

interface BatterySnackbarIconProps {
  device: BatteryNotificationDevice;
}

interface MessageStackItem {
  id: string;
  content: string | ReactNode;
  asMessage: true;
  autoHideDuration: number | null;
  onClose: () => void;
  persistent?: boolean;
}

interface ChildrenStackItem {
  id: string;
  content: ReactElement;
  asMessage: false;
  autoHideDuration: number | null;
  onClose: () => void;
  persistent?: boolean;
}

type StackItem = MessageStackItem | ChildrenStackItem;

interface BatteryStateSnackbarsProps {
  baseStackIndex?: number;
}

const BatterySnackbarIcon = ({ device }: BatterySnackbarIconProps) => {
  const Icon = device === 'controller' ? ControllerBatteryIcon : DriveBaseBatteryIcon;

  return (
    <Box
      sx={{
        color: 'common.white',
        display: 'inline-flex',
        flexShrink: 0,
        height: 24,
        width: 24,
        '& svg': {
          display: 'block',
        },
      }}
    >
      <Icon aria-hidden focusable="false" width={24} height={24} />
    </Box>
  );
};

const BatteryStateSnackbars = ({ baseStackIndex = 0 }: BatteryStateSnackbarsProps) => {
  const { notifications, onClose } = useBatteryNotifications();
  const connectionNotification = useConnectionNotification();
  const controllerFirmwareNotification = useControllerFirmwareUpdateNotification();

  const stackItems: StackItem[] = [
    ...(controllerFirmwareNotification.open
      ? [
          {
            id: 'controller-firmware-update',
            content: <ControllerFirmwareUpdateMessage message={controllerFirmwareNotification.message} />,
            asMessage: true as const,
            autoHideDuration: null,
            onClose: () => {},
            persistent: true,
          },
        ]
      : []),
    ...(connectionNotification.open
      ? [
          {
            id: 'connection-notification',
            content: connectionNotification.message,
            asMessage: true as const,
            autoHideDuration: CONNECTION_AUTO_HIDE_DURATION_MS,
            onClose: connectionNotification.onClose,
          },
        ]
      : []),
    ...notifications.map((notification) => ({
      id: notification.id,
      content: (
        <Alert icon={<BatterySnackbarIcon device={notification.device} />} severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      ),
      asMessage: false as const,
      autoHideDuration: AUTO_HIDE_DURATION_MS,
      onClose: () => onClose(notification.id),
    })),
  ];

  return (
    <>
      {stackItems.map((item, index) => (
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          autoHideDuration={item.autoHideDuration}
          key={item.id}
          onClose={(_event, reason) => {
            if (item.persistent && (reason === 'clickaway' || reason === 'escapeKeyDown')) {
              return;
            }
            if (reason === 'clickaway') {
              return;
            }
            item.onClose();
          }}
          open
          message={item.asMessage ? item.content : undefined}
          slotProps={{
            content: {
              sx: mergeSx(item.asMessage && typeof item.content === 'string' ? centeredPlainSnackbarContentSx : undefined, defaultSnackbarContentSx),
            },
          }}
          sx={bottomCenterSnackbarStackSx(baseStackIndex + index)}
        >
          {item.asMessage ? undefined : item.content}
        </Snackbar>
      ))}
    </>
  );
};

export default BatteryStateSnackbars;
